import { useEffect, useRef, useState } from "react";
import { Typography, Box, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import { useDebounce } from '../../hooks/useDebounce';
import { BrandCheckbox } from "../CoreComponents/BrandCheckbox";
import useAbortableFetch from '../../hooks/useAbortableFetch.js';
import { BrandSearchInput } from '../CoreComponents/BrandInput';
import BrandTable from '../CoreComponents/BrandTable.js';
import clsx from 'clsx';
import { IconButton, Tooltip } from '@material-ui/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDate } from "../utils/DateUtils.js";

const useStyles = makeStyles((theme) => ({
    filtersWrapper: {
        display: 'flex',
        columnGap: theme.spacing(2),
        alignItems: 'center',
        marginBottom: theme.spacing(3)
    },
    searchInput: {
        width: 450
    },
    tableContainer: {
        '& .MuiBox-root': {
            paddingBottom: theme.spacing(1),
            paddingTop: theme.spacing(1)
        }
    },
    subtitle: {
        marginBottom: theme.spacing(3)
    },
    fileNameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    datePickerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2)
    },
    filterLabel: {
        color: theme.palette.text.lightYellow,
    },
    showOnlyApplicableFilter: {
        '& .MuiFormControlLabel-root': {
            marginRight: '0px'
        }
    },
    tooltipText: {
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.primary.lightYellow}`,
        borderRadius: theme.spacing(2),
        textAlign: 'left',
        padding: theme.spacing(2)
    },
    tooltipAlertText: {
        color: theme.palette.primary.delete
    },
    marginLeft: {
        marginLeft: theme.spacing(2),
    },
    greenText: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main
    }
}));

const USED_CSV_REPORT_TOOLTIP_TEXT = <p>Used in previous Royalty reports.</p>;
const APPLICABLE_FILES_FILTER_TOOLTIP_TEXT_ = <p>The applicable files are the ones that are currently NOT included in any previously generated royalty reports.</p>;

export default function CsvStatementsStep({
    selectedFitlers,
    selectedCSVStatements,
    setSelectedCSVStatements
}) {
    const styles = useStyles();
    const abortableFetch = useAbortableFetch();

    const [searchField, setSearchField] = useState('');
    const searchFieldQuery = useDebounce(searchField, 500);
    const [items, setItems] = useState([]);
    const [sortBy, setSortBy] = useState('id');
    const [sortType, setSortType] = useState('ASC');
    const showOnlyApplicable = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const body = {
                payeeId: selectedFitlers.payeeId,
                payorId: selectedFitlers.payorId,
                startDate: selectedFitlers.startDate,
                endDate: selectedFitlers.endDate,
                filter: searchFieldQuery,
                sort: sortBy,
                type: sortType,
                showOnlyApplicable: showOnlyApplicable[0]
            };
            try {
                const fetchedData = await abortableFetch('POST',
                    '/api/royalty-statement/get-payee-csv-statements', {
                    body,
                });

                setItems(fetchedData ? fetchedData.statementsData : []);
                const preSelectedStatements = fetchedData.statementsData.reduce((filtered, currStatement) => {
                    if (!currStatement.disabled)
                        filtered.push(currStatement.id);
                    return filtered;
                }, []);
                setSelectedCSVStatements(preSelectedStatements);

            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [searchFieldQuery, sortBy, sortType, showOnlyApplicable[0], selectedFitlers.startDate, selectedFitlers.endDate])

    const [headCells] = useState([
        {
            id: 'accountHolderName',
            label: 'Account Holder',
            width: '20%',
            minWidth: '20%',
            CellRender: ({ rowData }) => {
                return (
                    <div className={styles.rowItem}>
                        {rowData.accountHolderName}
                    </div>
                )
            }
        },
        {
            id: 'reportingCompanyName',
            label: 'Reporting Company',
            width: '20%',
            minWidth: '20%',
            CellRender: ({ rowData }) => {
                return (
                    <div className={styles.rowItem}>
                        {rowData.reportingCompanyName}
                    </div>
                );
            },
        },
        {
            id: 'fileName',
            label: 'CSV File name',
            width: '30%',
            minWidth: '30%',
            popover: true,
            CellRender: ({ rowData }) => {
                return (
                    <div className={clsx(styles.rowItem, styles.fileNameCell)}>
                        {rowData.fileName}
                        {Boolean(rowData.alreadyUsed) &&
                            <Tooltip title={USED_CSV_REPORT_TOOLTIP_TEXT} arrow placement="bottom" classes={{ tooltip: styles.tooltipText }} >
                                <MonetizationOnIcon color='primary' fontSize='small' />
                            </Tooltip>
                        }
                    </div>
                );
            }
        },
    ]);

    return (
        <>
            <Typography className={styles.subtitle} variant='subtitle1'>View CSVs for statement.</Typography>
            <div className={styles.filtersWrapper}>
                <BrandSearchInput
                    placeholder={`Search for statement`}
                    value={searchField}
                    onChange={(event) => setSearchField(event.target.value)}
                    classes={{ input: styles.searchInput }}
                />
                <div className={styles.showOnlyApplicableFilter}>
                    <BrandCheckbox
                        $value={showOnlyApplicable}
                        label='Show only applicable files'
                    />
                    <Tooltip title={APPLICABLE_FILES_FILTER_TOOLTIP_TEXT_} arrow placement="bottom" classes={{ tooltip: styles.tooltipText }} >
                        <IconButton>
                            <InfoOutlinedIcon fontSize={'small'} />
                        </IconButton>
                    </Tooltip>
                </div>
                <div className={styles.datePickerContainer}>
                    <span className={styles.marginLeft}>Report Period: 
                        <span className={styles.greenText}>{formatDate(selectedFitlers.startDate)} - {formatDate(selectedFitlers.endDate)}</span>
                    </span>
                </div>
            </div>
            <div className={styles.tableContainer}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '50px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <BrandTable
                        checkboxless={false}
                        rows={items}
                        headCells={headCells}
                        hidePagination={true}
                        rowIsClickable={false}
                        setSortBy={setSortBy}
                        setSortType={setSortType}
                        selectedIds={selectedCSVStatements}
                        setSelectedIds={setSelectedCSVStatements}
                        stickyHeader={true}
                    />
                )}
            </div>
        </>
    );

}